import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner, Badge } from 'react-bootstrap';
import { Save, X, Store, User, Mail, Phone, MapPin, Palette, Lock, Eye, EyeOff, Check, Upload, Image } from 'lucide-react';
import { boutiqueService } from '../../services/boutiqueService';

const CreateBoutique = ({ boutique, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    // Données boutique
    name: '',
    description: '',
    address: '',
    phone: '',
    phone1: '', // ✅ AJOUT: Second numéro de téléphone (optionnel)
    email: '',
    primaryColor: '#3b82f6',
    secondaryColor: '#1f2937',
    
    // Données gestionnaire (seulement pour création)
    managerData: {
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      phone: '',
      password: '',
      confirmPassword: '',
      address: '',
      productAccessType: 'ALL' // ✅ AJOUT avec valeur par défaut

    }
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const isEditing = !!boutique;

  // Initialiser le formulaire pour édition
  useEffect(() => {
    if (boutique) {
      setFormData(prev => ({
        ...prev,
        name: boutique.name || '',
        description: boutique.description || '',
        address: boutique.address || '',
        phone: boutique.phone || '',
        phone1: boutique.phone1 || '', // ✅ AJOUT: Initialiser phone1 pour édition
        email: boutique.email || '',
        primaryColor: boutique.primaryColor || '#3b82f6',
        secondaryColor: boutique.secondaryColor || '#1f2937'
      }));
    }
  }, [boutique]);

  // Gestionnaire de changement pour les champs de boutique
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Nettoyer les erreurs lors de la saisie
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  
// À ajouter dans les fonctions existantes
const handleLogoSelect = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validation
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    setErrors(prev => ({ ...prev, logo: 'Format non supporté. Utilisez JPG, PNG ou WebP' }));
    return;
  }

  if (file.size > 5 * 1024 * 1024) { // 5MB
    setErrors(prev => ({ ...prev, logo: 'Fichier trop volumineux (5MB maximum)' }));
    return;
  }

  setLogoFile(file);
  setErrors(prev => ({ ...prev, logo: '' }));

  // Créer la prévisualisation
  const reader = new FileReader();
  reader.onload = (e) => {
    setLogoPreview(e.target.result);
  };
  reader.readAsDataURL(file);
};

const removeLogo = () => {
  setLogoFile(null);
  setLogoPreview(null);
  if (fileInputRef.current) {
    fileInputRef.current.value = '';
  }
};

  // Gestionnaire de changement pour les champs de gestionnaire
  const handleManagerChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      managerData: {
        ...prev.managerData,
        [name]: value
      }
    }));
    
    // Nettoyer les erreurs lors de la saisie
    const errorKey = `manager.${name}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  // ✅ Validation complète du formulaire
  const validateForm = () => {
    const newErrors = {};

    // === VALIDATION BOUTIQUE ===
    
    // Nom requis
    if (!formData.name?.trim()) {
      newErrors.name = 'Le nom de la boutique est requis';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Le nom doit contenir au moins 2 caractères';
    }

    // Adresse requise
    if (!formData.address?.trim()) {
      newErrors.address = 'L\'adresse est requise';
    } else if (formData.address.trim().length < 5) {
      newErrors.address = 'L\'adresse doit contenir au moins 5 caractères';
    }

    // Téléphone requis
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    } else if (!/^[\+]?[\d\s\-\(\)]{8,}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    // ✅ AJOUT: Validation phone1 (optionnel mais format valide si fourni)
    if (formData.phone1 && formData.phone1.trim() && !/^[\+]?[\d\s\-\(\)]{8,}$/.test(formData.phone1.trim())) {
      newErrors.phone1 = 'Format du second téléphone invalide';
    }

    // Email optionnel mais doit être valide si fourni
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    // Validation couleurs
    if (formData.primaryColor && !/^#[0-9A-F]{6}$/i.test(formData.primaryColor)) {
      newErrors.primaryColor = 'Couleur primaire invalide (format: #RRGGBB)';
    }

    if (formData.secondaryColor && !/^#[0-9A-F]{6}$/i.test(formData.secondaryColor)) {
      newErrors.secondaryColor = 'Couleur secondaire invalide (format: #RRGGBB)';
    }

    // === VALIDATION GESTIONNAIRE (seulement pour création) ===
    if (!isEditing) {
      console.log('🔍 Validation gestionnaire pour nouvelle boutique...', formData.managerData);
      
      if (!formData.managerData.firstName?.trim()) {
        newErrors['manager.firstName'] = 'Le prénom du gestionnaire est requis';
      } else if (formData.managerData.firstName.trim().length < 2) {
        newErrors['manager.firstName'] = 'Le prénom doit contenir au moins 2 caractères';
      }
      
      if (!formData.managerData.lastName?.trim()) {
        newErrors['manager.lastName'] = 'Le nom du gestionnaire est requis';
      } else if (formData.managerData.lastName.trim().length < 2) {
        newErrors['manager.lastName'] = 'Le nom doit contenir au moins 2 caractères';
      }
      
      if (!formData.managerData.email?.trim()) {
        newErrors['manager.email'] = 'L\'email du gestionnaire est requis';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.managerData.email)) {
        newErrors['manager.email'] = 'Format d\'email invalide';
      }
      
      if (!formData.managerData.username?.trim()) {
        newErrors['manager.username'] = 'Le nom d\'utilisateur est requis';
      } else if (formData.managerData.username.trim().length < 3) {
        newErrors['manager.username'] = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
      }
      
      if (!formData.managerData.phone?.trim()) {
        newErrors['manager.phone'] = 'Le téléphone du gestionnaire est requis';
      } else if (!/^[\+]?[\d\s\-\(\)]{8,}$/.test(formData.managerData.phone.trim())) {
        newErrors['manager.phone'] = 'Format de téléphone invalide';
      }
      
      if (!formData.managerData.password) {
        newErrors['manager.password'] = 'Le mot de passe est requis';
      } else if (formData.managerData.password.length < 6) {
        newErrors['manager.password'] = 'Le mot de passe doit contenir au moins 6 caractères';
      }
      
      if (formData.managerData.password !== formData.managerData.confirmPassword) {
        newErrors['manager.confirmPassword'] = 'Les mots de passe ne correspondent pas';
      }
    }

    console.log('❌ Erreurs de validation:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Soumission avec logging complet - CORRIGÉE avec managerData
  const handleSubmit = async (e) => {
  e.preventDefault();
  
  console.log('📝 Soumission du formulaire...');
  console.log('📋 Données actuelles:', formData);
  
  if (!validateForm()) {
    console.log('❌ Validation échouée, arrêt de la soumission');
    return;
  }

  console.log('✅ Validation réussie, envoi au service...');
  setLoading(true);
  
  try {
    let result;
    
    if (isEditing) {
      // ✅ Mode édition : seulement les données boutique
      const boutiqueDataToSend = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        address: formData.address.trim(),
        phone: formData.phone.trim(),
        phone1: formData.phone1?.trim() || null, // ✅ AJOUT: Inclure phone1 dans l'édition
        email: formData.email?.trim() || null,
        logoUrl: formData.logoUrl?.trim() || null,
        primaryColor: formData.primaryColor || '#3b82f6',
        secondaryColor: formData.secondaryColor || '#64748b'
      };
      
      console.log('🔄 Données pour mise à jour:', boutiqueDataToSend);
      result = await onSave(boutiqueDataToSend);
      
    } else {
      // ✅ Mode création : CORRECTION DU MAPPING
      const boutiqueDataToSend = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        address: formData.address.trim(),
        phone: formData.phone.trim(),
        phone1: formData.phone1?.trim() || null, // ✅ AJOUT: Inclure phone1 dans la création
        email: formData.email?.trim() || null,
        logoUrl: formData.logoUrl?.trim() || null,
        primaryColor: formData.primaryColor || '#3b82f6',
        secondaryColor: formData.secondaryColor || '#64748b',
        
        // ✅ CORRIGÉ: Mapping correct vers le format attendu par le backend
        manager_data: {
          first_name: formData.managerData.firstName.trim(),
          last_name: formData.managerData.lastName.trim(),
          email: formData.managerData.email.trim(),
          username: formData.managerData.username.trim(),
          phone: formData.managerData.phone.trim(),
          password: formData.managerData.password
        }
      };
      
      console.log('➕ Données pour création:', boutiqueDataToSend);
      result = await onSave(boutiqueDataToSend);
    }
    
    // ✅ Fermer le modal uniquement si succès
    if (result && result.success !== false) {
      console.log('✅ Opération réussie, fermeture du modal');
      onClose();
    } else {
      console.log('❌ Échec de l\'opération:', result);
      // Les erreurs seront affichées par le composant parent
    }

    if (result && result.success && logoFile && !isEditing) {
  try {
    console.log('📸 Upload du logo pour la nouvelle boutique...');
    const logoResponse = await boutiqueService.uploadLogo(
      result.data.id, 
      logoFile,
      (progress) => console.log('Upload progress:', progress)
    );
    
    if (logoResponse.success) {
      console.log('✅ Logo uploadé avec succès');
      result.data.logoUrl = logoResponse.data.logoUrl;
    } else {
      console.warn('⚠️ Erreur upload logo:', logoResponse.message);
      // Ne pas bloquer la création pour une erreur de logo
    }
  } catch (error) {
    console.warn('⚠️ Erreur upload logo:', error);
  }
}
    
  } catch (error) {
    console.error('❌ Erreur lors de la soumission:', error);
    setErrors({ 
      submit: error.message || 'Erreur lors de la soumission' 
    });
  } finally {
    setLoading(false);
  }
};

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Store size={20} className="text-primary" />
          {isEditing ? 'Modifier la boutique' : 'Créer une nouvelle boutique'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Form onSubmit={handleSubmit}>
          
          {/* Erreur générale */}
          {errors.submit && (
            <Alert variant="danger" className="mb-3">
              {errors.submit}
            </Alert>
          )}

          {/* === SECTION BOUTIQUE === */}
          <div className="mb-4">
            <h6 className="text-primary mb-3 d-flex align-items-center gap-2">
              <Store size={16} />
              Informations de la boutique
            </h6>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom de la boutique *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    isInvalid={!!errors.name}
                    placeholder="Ex: SmartBoutik Yoff"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Téléphone principal *</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    isInvalid={!!errors.phone}
                    placeholder="Ex: 33 890 12 34"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* ✅ AJOUT: Champ pour le second numéro de téléphone */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Second téléphone (optionnel)</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone1"
                    value={formData.phone1}
                    onChange={handleChange}
                    isInvalid={!!errors.phone1}
                    placeholder="Ex: 77 123 45 67"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phone1}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Numéro secondaire pour la boutique (facultatif)
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    placeholder="contact@boutique.sn (optionnel)"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Description de la boutique (optionnel)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Adresse *</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                isInvalid={!!errors.address}
                placeholder="Ex: Yoff Layenne, près du marché central"
              />
              <Form.Control.Feedback type="invalid">
                {errors.address}
              </Form.Control.Feedback>
            </Form.Group>

          </div>

          {/* === SECTION GESTIONNAIRE (seulement pour création) === */}
          {/* ✅ Section Gestionnaire (uniquement en mode création) */}
          {!isEditing && (
            <>
              <hr className="my-4" />
              <h5 className="mb-3">
                <User className="me-2" size={20} />
                Informations du Gestionnaire
              </h5>
              <Alert variant="info">
                <strong>Important :</strong> Le gestionnaire sera responsable de cette boutique et pourra gérer les employés et les produits.
              </Alert>

              <Row>
                {/* Prénom */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prénom *</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={formData.managerData.firstName}
                      onChange={handleManagerChange}
                      placeholder="Ex: Moussa"
                      isInvalid={!!errors['manager.firstName']}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors['manager.firstName']}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Nom */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom *</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={formData.managerData.lastName}
                      onChange={handleManagerChange}
                      placeholder="Ex: Diallo"
                      isInvalid={!!errors['manager.lastName']}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors['manager.lastName']}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Email */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email *</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.managerData.email}
                      onChange={handleManagerChange}
                      placeholder="manager@boutique.com"
                      isInvalid={!!errors['manager.email']}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors['manager.email']}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Téléphone */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Téléphone *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.managerData.phone}
                      onChange={handleManagerChange}
                      placeholder="+221 77 123 45 67"
                      isInvalid={!!errors['manager.phone']}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors['manager.phone']}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Nom d'utilisateur */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom d'utilisateur *</Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={formData.managerData.username}
                      onChange={handleManagerChange}
                      placeholder="Ex: mdiallo"
                      isInvalid={!!errors['manager.username']}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors['manager.username']}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* ✅ NOUVEAU CHAMP: Accès aux types de produits */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Accès aux types de produits *</Form.Label>
                    <Form.Select
                      name="productAccessType"
                      value={formData.managerData.productAccessType}
                      onChange={handleManagerChange}
                      required
                    >
                      <option value="ALL">📦🔧 Tous les produits (Stockables + Services)</option>
                      <option value="STOCKABLE">📦 Produits stockables uniquement</option>
                      <option value="SERVICE">🔧 Services uniquement</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Définit les types de produits que ce gestionnaire pourra gérer
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Mot de passe */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mot de passe *</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.managerData.password}
                        onChange={handleManagerChange}
                        placeholder="Minimum 6 caractères"
                        isInvalid={!!errors['manager.password']}
                        required
                      />
                      <Button
                        variant="link"
                        className="position-absolute end-0 top-0 text-muted"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ border: 'none', background: 'none' }}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </Button>
                    </div>
                    <Form.Control.Feedback type="invalid">
                      {errors['manager.password']}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Confirmer mot de passe */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Confirmer le mot de passe *</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.managerData.confirmPassword}
                        onChange={handleManagerChange}
                        placeholder="Retapez le mot de passe"
                        isInvalid={!!errors['manager.confirmPassword']}
                        required
                      />
                      <Button
                        variant="link"
                        className="position-absolute end-0 top-0 text-muted"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{ border: 'none', background: 'none' }}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </Button>
                    </div>
                    <Form.Control.Feedback type="invalid">
                      {errors['manager.confirmPassword']}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={loading}>
          <X size={16} className="me-1" />
          Annuler
        </Button>
        <Button 
          variant={isEditing ? "primary" : "success"} 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-1" />
              {isEditing ? 'Mise à jour...' : 'Création...'}
            </>
          ) : (
            <>
              <Save size={16} className="me-1" />
              {isEditing ? 'Mettre à jour' : 'Créer la boutique'}
            </>
          )}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default CreateBoutique;